// The two-step code, asked for by the CRM itself.
//
// ⚠️ ONLY THE SIGN-IN PAGE USED TO ASK (found 2026-10-05). login.js asks for
// the authenticator code after a password, and nothing else ever did. A reset
// or team-invite link signs straight into index.html (modules/password.js),
// and so does opening the CRM in a second tab after such a link, so an account
// with two-step sign-in turned on could be opened with its email alone.
// Clayton: "do both". Now the CRM asks whenever this session has not passed
// the code, by the same rule login.js applies:
//
//     the account has a verified authenticator (Supabase nextLevel = aal2),
//     this session has not passed it (currentLevel = aal1),
//     and this device is not trusted (modules/device-trust.js).
//
// ⚠️ "TRUST THIS DEVICE" STILL SKIPS IT, exactly as on the sign-in page. That
// is a choice the coach made on this computer, and asking anyway would make
// the box they ticked a lie.
//
// ⚠️ NO BACKUP CODE HERE. The sign-in page's backup-code path signs in again
// with the email AND password (mfa-backup-verify); someone who arrived from a
// reset email has no password to give. Sign out is offered instead.
//
// Above everything else on the page, the new-password box included
// (modules/password.js): a reset link with two-step sign-in on asks for the
// code first, and the password then saves without asking again.
//
// Imported by app.js for its side effects, the way mfa.js and password.js are.

import { getSupabase } from './config.js';
import { isDeviceTrusted, trustThisDevice } from './device-trust.js';

/**
 * Does this session still owe the two-step code?
 * @param {any} [sb] the Supabase client (the CRM's shared one by default)
 * @returns {Promise<{ owes: boolean, uid: string|null }>}
 */
export async function sessionOwesTheCode(sb = getSupabase()) {
  if (!sb) return { owes: false, uid: null };
  const { data } = await sb.auth.getSession();
  const uid = data?.session?.user?.id || null;
  if (!uid) return { owes: false, uid: null };
  const { data: aal } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (!(aal?.nextLevel === 'aal2' && aal?.currentLevel !== 'aal2')) return { owes: false, uid };
  if (isDeviceTrusted(uid)) return { owes: false, uid };
  return { owes: true, uid };
}

/**
 * The box. Closes only once the code is right (resolves true) or by signing
 * out. Escape does nothing.
 * @param {{ uid: string }} who
 * @returns {Promise<boolean>}
 */
export function openTwoStepGate({ uid }) {
  const existing = document.getElementById('two-step-gate');
  if (existing) return Promise.resolve(false);

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'two-step-gate';
  // Above crm-dialog-overlay (100002), so this comes before the password box.
  overlay.style.zIndex = '100003';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'two-step-title');
  overlay.innerHTML = `
    <form class="crm-dialog" id="two-step-form" novalidate>
      <h2 class="crm-dialog-title" id="two-step-title">Enter your two-step code</h2>
      <p class="crm-dialog-msg">Two-step sign-in is on for this account. Enter the 6-digit code from your authenticator app to keep going.</p>
      <div class="form-group">
        <label for="two-step-code">6-digit code</label>
        <input type="text" id="two-step-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" />
      </div>
      <label style="display:flex;align-items:center;gap:8px;margin-top:14px;font-size:var(--fs-sm);color:var(--text-muted);cursor:pointer;">
        <input type="checkbox" id="two-step-trust" /> Trust this device for 30 days
      </label>
      <div class="login-error hidden" id="two-step-error" role="alert" style="margin-top:14px;"></div>
      <div class="crm-dialog-actions">
        <button type="button" class="btn-secondary" id="two-step-signout">Sign out</button>
        <button type="submit" class="btn-primary" id="two-step-verify">Verify</button>
      </div>
    </form>`;
  document.body.appendChild(overlay);

  const $ = (sel) => overlay.querySelector(sel);
  const errorEl = $('#two-step-error');
  const verifyBtn = $('#two-step-verify');
  const show = (msg) => { errorEl.textContent = msg; errorEl.classList.remove('hidden'); };

  let resolveFn;
  const promise = new Promise((r) => { resolveFn = r; });

  $('#two-step-signout').addEventListener('click', async () => {
    try { await getSupabase().auth.signOut(); } catch { /* going to the sign-in page either way */ }
    window.location.href = 'login.html';
  });

  $('#two-step-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.classList.add('hidden');
    const code = $('#two-step-code').value.replace(/\D/g, '');
    if (code.length !== 6) { show('Enter the 6-digit code from your authenticator app.'); return; }
    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Checking…';
    try {
      const sb = getSupabase();
      const { data: factors } = await sb.auth.mfa.listFactors();
      const factor = (factors?.totp || []).find((f) => f.status === 'verified');
      if (!factor) { show("Couldn't find your authenticator. Sign out, then sign in again."); return; }
      const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code });
      if (error) { show("That code didn't work. Try the newest one from your app."); return; }
      if ($('#two-step-trust').checked) trustThisDevice(uid);
      overlay.remove();
      resolveFn(true);
    } catch (err) {
      console.warn('[two-step] verify failed:', /** @type {any} */ (err)?.message);
      show("Couldn't check the code. Try again.");
    } finally {
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Verify';
    }
  });

  setTimeout(() => $('#two-step-code')?.focus(), 0);
  return promise;
}

/** At boot: ask when the session owes the code. */
export async function askForTheCodeIfOwed() {
  try {
    const r = await sessionOwesTheCode();
    if (r.owes && r.uid) await openTwoStepGate({ uid: r.uid });
  } catch (e) {
    console.warn('[two-step] check failed:', /** @type {any} */ (e)?.message);
  }
}

// Same deferral as mfa.js: this module body runs before app.js reaches
// _config.setSupabaseRef(), and a microtask runs after it.
queueMicrotask(() => { askForTheCodeIfOwed(); });
